import { Field } from 'formik';
import { pathOr } from 'ramda';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { string } from 'yup';

import {
  COUNTRIES,
  COUNTRY_POSTAL_CODE_REGEX,
  COUNTRY_REGIONS,
  DEFAULT_REGION_LABEL,
} from '@frontend/addresses';

import messages from 'constants/messages';

import { Input, PhoneInput, Select } from './Inputs';

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  @media (min-width: 768px) {
    padding-left: 15px;
  }
`;

const Col = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;

  > .ant-form-item {
    display: block !important;
  }

  ${(props) => {
    if (props && props.formWidth < 500)
      return `
      width: 100%;
      .ant-form-item {
        display: block !important;
      }
      `;
    else
      return `
      @media (min-width: 768px) {
        width: 50%;
        padding-right: 15px;
      };

      @media (min-width: 900px) {
        width: 33.33%;
      }`;
  }}

  ${(props) => {
    if (props.isForSelect) {
      return `
        .ant-form-item {
          display: flex;
          flex-direction: column;
        }
        .ant-form-item-label {
          text-align: left;
        }`;
    }
  }}
`;

/*
Postal codes are required if:
1. The country requires postal codes
*/
const postalCodeValidation = function (value) {
  // 1. Check if postal code is required for this country
  const country = pathOr('', ['country'], this.parent);
  if (
    Object.prototype.hasOwnProperty.call(COUNTRY_POSTAL_CODE_REGEX, country) &&
    !value
  ) {
    // postal code is required and is not filled in
    return false;
  } else {
    return true;
  }
};

/*
States are required if:
1. The country requires states
*/
const stateValidation = function (value) {
  const country = pathOr('', ['country'], this.parent);
  if (COUNTRY_REGIONS[country] && !value) {
    // this country has states, but they are not selected
    return false;
  } else {
    return true;
  }
};

export const getAddressValidationRules = () => {
  return {
    address1: string().required(messages.REQUIRED),
    attention: string().required(messages.REQUIRED),
    businessName: string().required(messages.REQUIRED),
    city: string().required(messages.REQUIRED),
    country: string().required(messages.REQUIRED),
    postalCode: string()
      .test({
        test: postalCodeValidation,
        message: 'This field is required.',
      })
      .when(['country'], (selectedCountry, schema) => {
        if (
          Object.prototype.hasOwnProperty.call(
            COUNTRY_POSTAL_CODE_REGEX,
            selectedCountry,
          )
        ) {
          return schema.matches(
            COUNTRY_POSTAL_CODE_REGEX[selectedCountry],
            `Invalid ${pathOr(
              'Postal Code',
              [selectedCountry, 'postalCodeLabel'],
              COUNTRY_REGIONS,
            )}`,
          );
        }
      }),
    state: string()
      .test({
        test: stateValidation,
        message: 'This field is required.',
      })
      .when(['country'], (selectedCountry, schema) => {
        const countryRegion = COUNTRY_REGIONS[selectedCountry];
        if (countryRegion) {
          return schema.oneOf(
            countryRegion.regions.map((region) => region.abbreviation),
            `Invalid ${countryRegion.regionLabel ?? DEFAULT_REGION_LABEL}`,
          );
        }
      }),
    phone: string().nullable(),
  };
};

export const transformShippingInfoToValues = (data, defaultCountry = 'USA') => {
  return {
    address1: pathOr('', ['address', 'address1'], data),
    address2: pathOr('', ['address', 'address2'], data),
    attention: pathOr('', ['attention'], data),
    businessName: pathOr('', ['businessName'], data),
    city: pathOr('', ['address', 'city'], data),
    country: pathOr(defaultCountry, ['address', 'country', 'abbr'], data),
    phone: pathOr('', ['phone'], data),
    phoneExt: pathOr('', ['phoneExt'], data),
    state: pathOr(undefined, ['address', 'state', 'abbr'], data),
    postalCode: pathOr('', ['address', 'postalCode'], data),
  };
};

export const transformValuesToShippingInfo = (data) => ({
  attention: pathOr('', ['attention'], data),
  businessName: pathOr('', ['businessName'], data),
  phone: pathOr('', ['phone'], data),
  phoneExt: pathOr('', ['phoneExt'], data),
  address: {
    address1: pathOr('', ['address1'], data),
    address2: pathOr('', ['address2'], data),
    postalCode: pathOr('', ['postalCode'], data),
    city: pathOr('', ['city'], data),
    state: pathOr(false, ['state'], data)
      ? {
          abbr: pathOr('', ['state'], data),
        }
      : null,
    country: {
      abbr: pathOr('', ['country'], data),
    },
  },
});

const StateSelect = ({ className, countryRegion, ...props }) => {
  const options =
    countryRegion &&
    countryRegion.regions.map(({ abbreviation, name }) => ({
      label: name,
      value: abbreviation,
    }));
  const value = pathOr('', ['field', 'value'], props);
  return (
    <Select
      className={className}
      filterOption={(option, inputValueIn) => {
        const inputValue = inputValueIn.trim().toLowerCase();
        return (
          option.label.toLowerCase().indexOf(inputValue) === 0 ||
          option.value.toLowerCase().indexOf(inputValue) === 0
        );
      }}
      options={options || []}
      key={value}
      {...props}
    />
  );
};

/**
 * @deprecated
 */
const AddressForm = ({
  isPostalCodeDisabled,
  isCountryDisabled,
  isReadOnly,
  country = 'USA',
}) => {
  const formRef = useRef(null);
  const [formWidth, setFormWidth] = useState(undefined);
  useEffect(() => {
    const handleResize = () => {
      setFormWidth(formRef.current.offsetWidth);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [formRef]);

  useEffect(() => {
    setFormWidth(formRef.current.offsetWidth);
  }, []);

  const countryRegion = COUNTRY_REGIONS[country];

  return (
    <div ref={formRef}>
      <Row formWidth={formWidth}>
        <Col formWidth={formWidth}>
          <Field
            component={Input}
            autoComplete="none"
            name="attention"
            label="Attention *"
            readOnly={isReadOnly}
          />
        </Col>

        <Col formWidth={formWidth}>
          <Field
            component={Input}
            autoComplete="none"
            name="businessName"
            label="Business Name *"
            readOnly={isReadOnly}
          />
        </Col>

        <Col formWidth={formWidth}>
          <Field
            component={PhoneInput}
            autoComplete="none"
            name="phone"
            label="Phone"
            readOnly={isReadOnly}
          />
          <Field
            addonBefore="ext."
            component={Input}
            autoComplete="none"
            readOnly={isReadOnly}
            name="phoneExt"
          />
        </Col>

        <Col formWidth={formWidth}>
          <Field
            component={Input}
            autoComplete="none"
            readOnly={isReadOnly}
            name="address1"
            label="Street Address 1 *"
          />
        </Col>

        <Col formWidth={formWidth}>
          <Field
            component={Input}
            autoComplete="none"
            readOnly={isReadOnly}
            name="address2"
            label="Street Address 2"
          />
        </Col>

        <Col formWidth={formWidth}>
          <Field
            component={Input}
            autoComplete="none"
            readOnly={isReadOnly}
            name="city"
            label="City *"
          />
        </Col>

        <Col formWidth={formWidth} isForSelect={true}>
          <Field
            component={StateSelect}
            countryRegion={countryRegion}
            autoComplete="none"
            disabled={isReadOnly || !countryRegion}
            name="state"
            label={`${pathOr(
              'State/Province',
              ['regionLabel'],
              countryRegion,
            )}${countryRegion ? ' *' : ''}`}
            placeholder={
              countryRegion
                ? `Select ${pathOr(
                    'State/Province',
                    ['regionLabel'],
                    countryRegion,
                  )}`
                : ''
            }
          />
        </Col>

        <Col formWidth={formWidth}>
          <Field
            component={Input}
            readOnly={isReadOnly}
            autoComplete="none"
            name="postalCode"
            label={`${pathOr(
              'Postal Code',
              ['postalCodeLabel'],
              countryRegion,
            )}${
              Object.prototype.hasOwnProperty.call(
                COUNTRY_POSTAL_CODE_REGEX,
                country,
              )
                ? ' *'
                : ''
            }`}
            disabled={isPostalCodeDisabled}
          />
        </Col>

        <Col formWidth={formWidth} isForSelect={true}>
          <Field name="country" label="Country *">
            {({ form, ...props }) => {
              return (
                <Select
                  form={form}
                  {...props}
                  label="Country *"
                  autoComplete="none"
                  disabled={isCountryDisabled || isReadOnly}
                  placeholder=""
                  options={COUNTRIES.map(({ abbreviation, name }) => ({
                    label: name,
                    value: abbreviation,
                  }))}
                  onChange={(value) => {
                    const currentCountry = pathOr(
                      '',
                      ['values', 'country'],
                      form,
                    );
                    if (value && value !== currentCountry) {
                      form.setFieldValue('state', '');
                      form.setFieldValue('country', value);
                      form.setFieldTouched('country', true);
                    }
                  }}
                />
              );
            }}
          </Field>
        </Col>
      </Row>
    </div>
  );
};

export default AddressForm;
